import React, { useState, useRef, ChangeEvent } from "react";

import { Formik, Form, Field, ErrorMessage } from "formik";

import { BlobServiceClient } from "@azure/storage-blob";

import * as Yup from "yup";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  TextField,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  styled,
  Button,
  Stack,
  Chip,
  Switch,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  alpha,
  Typography,
  DialogContent,
  DialogActions,
  DialogTitle,
  Dialog,
  DialogProps,
  Grid,
  Link,
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
} from "@mui/material";

import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";

import SensitiveIconSvg from "../../../assets/images/icon-sensitive.svg";
import NonSensitiveIconBlueSvg from "../../../assets/images/icon-non-sensitiveBlue.svg";
import DeleteIconSvg from "../../../assets/images/icon-delete.svg";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

import {
  useUpdateClientFileChunksUpload,
  useUpdateClientFileUpload,
} from "../../hooks";

import {
  AllowedFileType,
  allowedTypes,
  forbiddenExtensions,
  MAX_FILE_SIZE,
  allowedExtensions,
  extensionToMimeType,
} from "../../helpers/constants";
import { colors } from "../../../assets/styles/theme";

interface FileWithSensitivity {
  file: File;
  isSensitive: boolean;
  base64: string;
  id?: number;
  filePath?: string[];
  systemCreatedDateTime?: any;
  createdBy?: string;
  isExisting?: boolean;
  isNew?: boolean;
  isDelete?: boolean;
  fileFixedIndex: any;
}

interface ResearchExperimentsFormProps {
  uploadType: any;
  handleSave?: (files: any, values: any, id: any, batchId: any) => void;
  clients: any;
  apiError: any;
  industriesDataOptions: any;
  handleUploadCancel?: () => void;
  isLoading: any;
  initialFileValues: any;
  editData: any;
  isEditMode: any;
  fromScreenData: any;
  apiErrorText: any;
}

const sasToken =
  "sv=2024-11-04&ss=bfqt&srt=sc&sp=rwdlacupiytfx&se=2525-04-11T02:24:46Z&st=2025-04-10T18:24:46Z&spr=https&sig=2dTbGvndpmNN%2FF0I8mEeJ5%2B71Ss4eD0NvAB3DNz%2Bn%2Fg%3D"; // From Azure Portal or your backend
const containerName = "fileuploads";
const storageAccountName = "stdcportaldeveastus2blob";

const ResearchExperimentsForm: React.FC<ResearchExperimentsFormProps> = ({
  handleSave,
  uploadType,
  clients,
  apiError,
  industriesDataOptions,
  handleUploadCancel,
  isLoading,
  initialFileValues,
  isEditMode = false,
  editData,
  fromScreenData,
  apiErrorText,
}) => {
  const minDate = dayjs("2000-01-01");
  const maxDate = dayjs("2099-12-31");

  
  const [filesCount, setFilesCount] = useState(0);

  const [batchId, setBatchId] = React.useState<string>(() => {
    return `${Date.now()}${Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0")}`;
  });

  const [deleteLoader, setDeleteLoader] = React.useState<any>(false);
  const [statusLoader, setStatusLoader] = React.useState<any>(false);

  const {
    mutate: uploadClientFileChunks,
    isPending: isUploadClientFileChunkPending,
  } = useUpdateClientFileChunksUpload();

  const { mutate: uploadClientFile, isPending: isUploadClientFilePending } =
    useUpdateClientFileChunksUpload();


  // Dynamic validation schema based on uploadType
  const getValidationSchema = () => {
    // Base schema that always applies
    const fileUploadSchema = {
      client: Yup.object()
        .shape({
          label: Yup.string().required("Client name is required"),
          value: Yup.string().required("Client value is required"),
        })
        .required("Client is required"),
    };

    // Schema for test type
    const researchExpSchema = {
      ...fileUploadSchema,
      title: Yup.string().required("Title is required"),
      testAndLearnVehicle: Yup.string().required(
        "Test and Learn Vehicle is required"
      ),
      hypothesis: Yup.string().required("Hypothesis is required"),
      background: Yup.string().required("Background is required"),
      testStoresOrGeography: Yup.string().required("Test stores is required"),
      controlStoresOrGeography: Yup.string().required(
        "Control stores is required"
      ),
      measuredKPI: Yup.string().required("Measured KPI is required"),
      testDesign: Yup.string().required("Test design is required"),
      testStartDate: Yup.mixed()
        .required("Required*")
        .test("is-dayjs", "Start date is required", (value: any) => {
          return (
            (value && dayjs.isDayjs(value) && value.isValid()) ||
            (value instanceof Date && !isNaN(value.getTime()))
          );
        })
        .test(
          "min-date",
          "The selected date is too far in the past",
          (value: any) => {
            return (
              dayjs(value).isAfter(minDate) || dayjs(value).isSame(minDate)
            );
          }
        )
        .test(
          "max-date",
          "The selected date is too far in the future",
          (value) => {
            return (
              dayjs(value).isBefore(maxDate) || dayjs(value).isSame(maxDate)
            );
          }
        ),
      testEndDate: Yup.mixed()
        .required("Required*")
        .test("is-dayjs", "End date is required", (value: any) => {
          return (
            (value && dayjs.isDayjs(value) && value.isValid()) ||
            (value instanceof Date && !isNaN(value.getTime()))
          );
        })
        .test(
          "min-date",
          "The selected date is too far in the past",
          (value: any) => {
            return (
              dayjs(value).isAfter(minDate) || dayjs(value).isSame(minDate)
            );
          }
        )
        .test(
          "max-date",
          "The selected date is too far in the future",
          (value: any) => {
            return (
              dayjs(value).isBefore(maxDate) || dayjs(value).isSame(maxDate)
            );
          }
        )
        .test(
          "after-start-date",
          "End date must be after start date",
          function (value: any) {
            const { testStartDate } = this.parent;
            if (!testStartDate || !value) return true; // Skip if either date is missing

            const start = dayjs(testStartDate);
            const end = dayjs(value);

            return end.isAfter(start) || end.isSame(start);
          }
        ),
      results: Yup.string().required("Result is required"),
      statisticalSignificance: Yup.string().required(
        "Statistical Significance is required"
      ),
    };
    // Return the appropriate schema based on type
    return uploadType === "research" ? researchExpSchema : fileUploadSchema;
  };


  // Prepare initial form values based on edit mode
  const getInitialValues = () => {
    if (isEditMode && editData) {
      // For research type, extract all the relevant fields
      if (uploadType === "research" && editData) {
        const researchData = editData;
        return {
          client: {
            label: researchData?.clientName,
            value: researchData?.clientId.toString(),
          },
          testAndLearnVehicle: researchData?.testAndLearnVehicle || "",
          title: researchData?.title || "",
          hypothesis: researchData?.hypothesis || "",
          background: researchData?.background || "",
          testStoresOrGeography: researchData?.testStoresOrGeography || "",
          controlStoresOrGeography:
            researchData?.controlStoresOrGeography || "",
          measuredKPI: researchData?.measuredKPI || "",
          testDesign: researchData?.testDesign || "",
          testStartDate: researchData?.testStartDate
            ? dayjs(researchData?.testStartDate)
            : null,
          testEndDate: researchData?.testEndDate
            ? dayjs(researchData?.testEndDate)
            : null,
          results: researchData?.results || "",
          statisticalSignificance: researchData?.statisticalSignificance || "",
          recommendationsOrNotes: researchData?.recommendationsOrNotes || "",
        };
      } else {
        // For basic file upload, just the client
        return {
          client: {
            label: editData?.clientName,
            value: editData?.clientId.toString(),
          },
          testAndLearnVehicle: "",
          title: "",
          hypothesis: "",
          background: "",
          testStoresOrGeography: "",
          controlStoresOrGeography: "",
          measuredKPI: "",
          testDesign: "",
          testStartDate: null,
          testEndDate: null,
          results: "",
          statisticalSignificance: "",
          recommendationsOrNotes: "",
        };
      }
    }

    // Default initial values for new form
    return {
      client: {
        label:
          fromScreenData == "ManagePlatform"
            ? initialFileValues.client.label
            : "",
        value:
          fromScreenData == "ManagePlatform"
            ? initialFileValues.client.value
            : "",
      },
      testAndLearnVehicle: "",
      title: "",
      hypothesis: "",
      background: "",
      testStoresOrGeography: "",
      controlStoresOrGeography: "",
      measuredKPI: "",
      testDesign: "",
      testStartDate: null,
      testEndDate: null,
      results: "",
      statisticalSignificance: "",
      recommendationsOrNotes: "",
    };
  };

  const uploadFile = async (file: any) => {
    try {
      const blobServiceClient = new BlobServiceClient(
        `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);

      const blockBlobClient = containerClient.getBlockBlobClient(file[0].name);
      await blockBlobClient.uploadBrowserData(file[0]);

      console.log("Upload successful!");
    } catch (error: any) {
      console.error("Upload error:", error.message);
      console.log("Upload failed.");
    }
  };

  // Initial form values
  const initialValues = {
    client: {
      label: "",
      value: "",
    },
    testAndLearnVehicle: "",
    title: "",
    hypothesis: "",
    background: "",
    testStoresOrGeography: "",
    controlStoresOrGeography: "",
    measuredKPI: "",
    testDesign: "",
    testStartDate: null,
    testEndDate: null,
    results: "",
    statisticalSignificance: "",
    recommendationsOrNotes: "",
  };
  // Mapping of file extensions to MIME types
  const mimeTypes: { [key: string]: string } = {
    ".pdf": "application/pdf",
    ".png": "image/png",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".gif": "image/gif",
    ".txt": "text/plain",
    ".csv": "text/csv",
    ".xls": "application/vnd.ms-excel",
    ".xlsx":
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".doc": "application/msword",
    ".docx":
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".ppt": "application/vnd.ms-powerpoint",
    ".pptx":
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".zip": "application/zip",
    ".mp3": "audio/mpeg",
    ".mp4": "video/mp4",
    ".webm": "video/webm",
    ".json": "application/json",
    ".js": "application/javascript",
    ".html": "text/html",
    ".css": "text/css",
    // Add more as needed
  };

  // Extract the file extension from the file path
  const getFileExtension = (filePath: string): string => {
    return filePath
      .slice(((filePath.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();
  };

  // Get the MIME type based on the file extension
  const getMimeTypeFromFilePath = (filePath: string): string => {
    const extension = `.${getFileExtension(filePath)}`;
    return mimeTypes[extension] || "application/octet-stream"; // Default to "application/octet-stream" for unknown types
  };

  // Handle form submission
  const handleSubmit = (values: any, { setSubmitting }: any) => {
    // In a real app, you'd send this data to your backend
    console.log("Form values:", values);
    alert(JSON.stringify(values, null, 2));
    setSubmitting(false);
  };

  // Options for dropdowns
  const vehicleOptions = [
    "Video",
    "Audio",
    "Display",
    "Social Media",
    "Search",
    "Email",
    "Creative",
    "Multiple",
    "Landing Page",
    "Other",
  ];

  const statisticalOptions = [
    "Yes at 95% or Greater",
    "Yes at 90-95%",
    "No",
    "Not Tested",
  ];

  const kpiOptions = [
    "Site Traffic",
    "Site Conversions",
    "Site Sales",
    "Store Traffic",
    "Store Purchases",
    "Store Sales",
    "Brand Searches",
    "Clicks",
    "Total Sales",
    "Total Conversions",
    "Total Leads",
    "Other",
  ];

  const [files, setFiles] = useState<FileWithSensitivity[]>([]);
  const [newFiles, setNewFiles] = useState<FileWithSensitivity[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<any>({});
  const [error, setError] = useState<string | null>(apiErrorText);
  const [success, setSuccess] = useState<any | null>(false);

  const [successText, setSuccessText] = useState<any | null>(false);

  const [id, setId] = useState<any | null>(null);
  const [isFileUploadingStatus, setIsFileUploadingStatus] =
    useState<boolean>(false);

  const [loader, setLoader] = useState<any | null>(false);

  React.useEffect(() => {
    setLoader(isLoading);
  }, [isLoading]);

  const firstName = window.localStorage.getItem("FIRST_NAME");
  const lastName = window.localStorage.getItem("LAST_NAME");

  let UserName = "";

  if (firstName && lastName && firstName != "null" && lastName != "null") {
    UserName = `${lastName}, ${firstName}`;
  } else if (firstName && firstName != "null") {
    UserName = firstName;
  } else if (lastName && lastName != "null") {
    UserName = lastName;
  } else {
    UserName = "User";
  }

  const fileDetailWrapperRef: any = useRef(null);

  const [fileStats, setFileStats] = useState({
    count: 0,         // Number of files
    totalSize: 0      // Total size in bytes
  });

  const fileStatsRef = useRef({
    count: 0,         // Active file count
    totalSize: 0      // Total size in bytes
  });

  // Scroll to top when files or uploadingFiles change
  React.useEffect(() => {
    if (fileDetailWrapperRef.current) {
      fileDetailWrapperRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [files, uploadingFiles]);

  React.useEffect(() => {
    if (isEditMode && editData) {
      // Create a file object for each existing file
      if (editData?.fileDetails && editData?.fileDetails.length > 0) {
        const existingFiles: FileWithSensitivity[] = editData.fileDetails.map(
          (fileDetail: any, index: number) => {
            const filePath = fileDetail.filePath;

            // Use fileIndex from server if available, otherwise use index+1
            const fileIndex = fileDetail.fileIndex || index + 1;
            fileStatsRef.current = {
              count: fileStatsRef.current.count + 1,
              totalSize: fileStatsRef.current.totalSize + (fileDetail?.size || 0)
            };

            return {
              file: {
                name: fileDetail.fileName,
                type: getMimeTypeFromFilePath(filePath) || "",
                size: 0, // You might want to fetch the actual file size if available
              } as File,
              isSensitive: editData?.isSensitive || false,
              base64: "", // You can populate this if you have the base64 data
              id: fileDetail?.fileId,
              filePath: filePath,
              systemCreatedDateTime: fileDetail?.createdDateTime,
              createdBy: fileDetail?.createdBy,
              isExisting: true,
              isNew: false,
              isDelete: false,
              fileFixedIndex: fileIndex, // Add the fileFixedIndex property
            };
          }
        );

        setFiles(existingFiles);
        setNewFiles(existingFiles);

        // Initialize highestIndexRef after files are set
        // We can do this in a separate useEffect that depends on files
      } else if (
        editData?.fileDetails == null ||
        (editData?.fileDetails && editData?.fileDetails.length == 0)
      ) {
        setFiles([]);
        setNewFiles([]);
        // Reset highestIndexRef to 0 since no files exist
        highestIndexRef.current = 0;
      }
      setId(uploadType === "research" ? editData?.experimentId : editData?.id);
    }
  }, [isEditMode, editData]);

  // Add another useEffect to initialize highestIndexRef whenever files change
  React.useEffect(() => {
    initializeHighestIndexRef();
  }, [files]);

  React.useEffect(() => {
    apiError && setError(apiErrorText);
  }, [apiError, apiErrorText]);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          const base64String = reader.result.toString().split(",")[1];
          resolve(base64String);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const simulateUpload = async (
    file: File
  ): Promise<{ file: File; base64: string }> => {
    return new Promise((resolve) => {
      const fileId: string = `${file.name}-${Date.now()}`;

      // Set file as uploading
      setUploadingFiles((prev: any) => ({
        ...prev,
        [fileId]: { isUploading: true, filename: file.name },
      }));

      // Convert file to base64 while "uploading"
      convertFileToBase64(file).then((base64String) => {
        // Simulate network delay (e.g., 1.5 seconds)
        setTimeout(() => {
          // Mark upload as complete by removing from uploading state
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          resolve({ file, base64: base64String });
        }, 1500); // 1.5 seconds for simulation
      });
    });
  };

  // Flatten the arrays for easier checking
  const allAllowedExtensions = Object.values(allowedExtensions).flat();

  const [pendingFilesCount, setPendingFilesCount] = useState(0);

 

  const validateFile = (
    file: File,
    totalSize: number = 0,
    currentFileCount: number = 0
  ): string | null => {
    const MAX_FILE_COUNT = 5;
    const MAX_FILE_SIZE = 25;
    const MAX_TOTAL_SIZE = 100;

    console.log("currentFileCount", currentFileCount);

    console.log('totalSize', totalSize)
    
    if (currentFileCount >= MAX_FILE_COUNT) {
      return `Maximum number of files (${MAX_FILE_COUNT}) reached`;
    }

    // Get file extension
    const fileNameParts = file.name.split(".");
    const extension =
      fileNameParts.length > 1 ? `.${fileNameParts.pop()?.toLowerCase()}` : "";


    // Check if extension is allowed
    if (!allAllowedExtensions.includes(extension)) {
      return `File extension is not allowed`;
    }

    // If file.type is missing or empty, allow the file if the extension is in the allowed list
    if (!file.type) {
      console.log("File type is missing, but extension is allowed");
      // Proceed to size checks
    } else {
      // Check MIME type if we have a mapping for this extension
      const expectedMimeType = extensionToMimeType[extension];
      if (expectedMimeType) {
        const fileMimeTypeParts = file.type.split("/");
        const expectedMimeTypeParts = expectedMimeType.split("/");


        const isMimeTypeMatch =
          file.type === expectedMimeType ||
          // Generic match for main type
          (fileMimeTypeParts[0] === expectedMimeTypeParts[0] &&
            file.type.includes(expectedMimeTypeParts[1]));


        if (!isMimeTypeMatch) {
          // Special case for CSV which can be reported as text/csv or application/csv
          if (
            extension === ".csv" &&
            (file.type === "text/csv" || file.type === "application/csv")
          ) {
            // Allow both common MIME types for CSV
          }
          // Special case for certain Office files which might have variations in reporting
          else if (
            (extension.startsWith(".doc") ||
              extension.startsWith(".xls") ||
              extension.startsWith(".ppt")) &&
            file.type.includes("officedocument")
          ) {
            // Allow Office files with officedocument in the MIME type
          } else {
            return `File MIME type doesn't match expected type`;
          }
        }
      }
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
      return `File size exceeds ${MAX_FILE_SIZE}MB limit`;
    }

    // Check total file size (current file + previously added files)
    const newTotalSize = totalSize + file.size;
    if (newTotalSize > MAX_TOTAL_SIZE * 1024 * 1024) {
      return `Files size would exceed ${MAX_TOTAL_SIZE}MB limit`;
    }

    return null; // File is valid
  };

  const handleFileUploadMain = async (
    selectedFiles: any | null
  ): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    try {
      const MAX_FILE_COUNT = 5;

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum
      if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${selectedFiles.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${selectedFiles.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Track how many files we've successfully added
      let successfullyAddedCount = 0;

      console.log("currentFileCount", currentFileCount);

      // Process each file independently
      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles[i];

        console.log("successfullyAddedCount", successfullyAddedCount);

        // Check if adding this file would exceed the maximum (including files added in this loop)
        if (currentFileCount + successfullyAddedCount >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break; // Stop processing more files
        }

        // Calculate current total size
        const currentFiles = [...files];
        let totalSize = currentFiles.reduce((sum, fileObj) => {
          if (
            fileObj &&
            fileObj.isDelete === false &&
            fileObj.file &&
            typeof fileObj.file.size === "number"
          ) {
            return sum + fileObj.file.size;
          }
          return sum;
        }, 0);

        // Validation...
        const validationError = validateFile(
          file,
          totalSize,
          currentFileCount + successfullyAddedCount
        );

        if (validationError) {
          setError(validationError);
          setTimeout(() => setError(null), 3000);
          continue;
        }

        // Process file...
        try {
          const { file: uploadedFile, base64 } = await simulateUpload(file);

          const fileWithSensitivity: FileWithSensitivity = {
            file: uploadedFile,
            isSensitive: false,
            base64: base64,
            systemCreatedDateTime: new Date(),
            isNew: true,
            isDelete: false,
          };

          setFiles((prevFiles: any) => {
            // Do one final check before actually adding the file
            const currentCount = prevFiles.filter(
              (f: any) => f && f.isDelete === false
            ).length;
            if (currentCount >= MAX_FILE_COUNT) {
              setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
              setTimeout(() => setError(null), 3000);
              return prevFiles;
            }
            return [...prevFiles, fileWithSensitivity];
          });
          successfullyAddedCount++;

          setSuccess(true);
          setTimeout(() => setSuccess(null), 3000);
          setError(null);
        } catch (err) {
          setError(`Error uploading ${file.name}`);
          setTimeout(() => setError(null), 3000);
          setSuccess(false);
        }
      }
    } finally {
      // Clean up if needed
    }
  };

  const convertToBase64 = async (
    file: File
  ): Promise<{ file: File; base64: string }> => {
    return new Promise((resolve) => {
      // Convert file to base64
      convertFileToBase64(file).then((base64String) => {
        resolve({ file, base64: base64String });
      });
    });
  };

  // Add these at the top of your component or in a relevant scope
  const [isUploadings, setIsUploading] = useState<boolean>(false);
  const uploadQueue = useRef<Array<File[]>>([]);
  const processingQueue = useRef<boolean>(false);
  // Add a new ref to track the highest index
  const highestIndexRef = useRef<number>(0);

  // Function to process the upload queue
  const processUploadQueue1 = async (): Promise<void> => {
    if (processingQueue.current) {
      return; // Already processing the queue
    }

    if (uploadQueue.current.length === 0) {
      return; // Nothing to process
    }

    try {
      processingQueue.current = true;

      while (uploadQueue.current.length > 0) {
        const nextFileBatch = uploadQueue.current.shift();
        if (nextFileBatch && nextFileBatch.length > 0) {
          setIsUploading(true);
          await processFileUpload(nextFileBatch);
          setIsUploading(false);
        }
      }
    } finally {
      processingQueue.current = false;
    }
  };

  const processUploadQueue = async (): Promise<void> => {
    if (processingQueue.current) {
      return; // Already processing the queue
    }
  
    if (uploadQueue.current.length === 0) {
      return; // Nothing to process
    }
  
    try {
      processingQueue.current = true;

      console.log("uploadQueue",uploadQueue)
      console.log("processingQueue",processingQueue)
      
      // Important: Calculate how many total files we could potentially upload from all batches
      const MAX_FILE_COUNT = 5;
      
      // Get current file count (excluding deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;
      
      // If we're already at max, clear the queue and exit
      if (currentFileCount >= MAX_FILE_COUNT) {
        setError(`Maximum number of files (${MAX_FILE_COUNT}) already reached.`);
        setTimeout(() => setError(null), 3000);
        uploadQueue.current = []; // Clear the queue
        return;
      }
      
      // Calculate available slots
      const availableSlots = MAX_FILE_COUNT - currentFileCount;
      
      // Count total files in all queued batches
      let totalQueuedFiles = 0;
      uploadQueue.current.forEach(batch => {
        totalQueuedFiles += batch.length;
      });

      console.log("availableSlots",availableSlots)

      console.log("totalQueuedFiles",totalQueuedFiles)

      console.log("uploadQueue.current",uploadQueue.current)
      
      // If total queued exceeds available slots, we need to restructure the queue
      if (totalQueuedFiles > availableSlots) {
        setError(
          `Can only process ${availableSlots} more files to stay within the limit of ${MAX_FILE_COUNT}.`
        );
        setTimeout(() => setError(null), 3000);
        
        // Create a single flat array of all files from all batches
        const allFiles = uploadQueue.current.flat();
        
        // Take only what we can fit
        const filesToKeep = allFiles.slice(0, availableSlots);
        
        // Replace entire queue with a single batch containing only what we can process
        uploadQueue.current = filesToKeep.length > 0 ? [filesToKeep] : [];
      }
      
      // Now process each batch one at a time (should be at most one batch after our restructuring)
      while (uploadQueue.current.length > 0) {
        const nextFileBatch = uploadQueue.current.shift();
        if (nextFileBatch && nextFileBatch.length > 0) {
          setIsUploading(true);
          await processFileUpload(nextFileBatch);
          setIsUploading(false);
        }
      }
    } finally {
      processingQueue.current = false;
    }
  };

  // Function to initialize the highest index ref from the current files
  // Call this in useEffect once when component mounts
  const initializeHighestIndexRef = () => {
    const highestIndex = files.reduce((highest, file) => {
      if (file && typeof file.fileFixedIndex === "number") {
        return Math.max(highest, file.fileFixedIndex);
      }
      return highest;
    }, 0);
    highestIndexRef.current = highestIndex;
  };

  

  const handleFileUpload = async (selectedFiles: any | null): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;
    
    const MAX_FILE_COUNT = 5;
    
    // Get current file count (excluding deleted files)
    const currentFileCount = files.filter(file => file && file.isDelete === false).length;
    
    // Get count of files already in the queue
    const queuedFileCount = uploadQueue.current.reduce((sum, batch) => sum + batch.length, 0);

    const filesBeingUploaded = Object.keys(uploadingFiles).length;

    // Check if this new batch would exceed the limit when combined with current files and queue
    const potentialTotalCount = currentFileCount + queuedFileCount + selectedFiles.length;
    
    if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
      setError(
        `Maximum number of files is ${MAX_FILE_COUNT}.`
      );
      setTimeout(() => setError(null), 3000);
      return;
    }
  
    // Add files to the queue
    uploadQueue.current.push(Array.from(selectedFiles));


    
    console.log(
      `Added ${selectedFiles.length} files to upload queue. Queue size: ${uploadQueue.current.length}`
    );
  
    // Start processing if not already running
    processUploadQueue();
  };

  // Modified handleFileUpload function that adds to queue
  const handleFileUpload1 = async (selectedFiles: any | null): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    // Add files to the queue
    uploadQueue.current.push(Array.from(selectedFiles));
    console.log(
      `Added ${selectedFiles.length} files to upload queue. Queue size: ${uploadQueue.current.length}`
    );

    // Start processing if not already running
    processUploadQueue();
  };

  

  // The actual file processing function (moved from original handleFileUpload)
  const processFileUpload = async (filesToProcess: File[]): Promise<void> => {
    console.log('processFileUpload', filesToProcess);
    console.log('file size', fileStatsRef)

    try {
      const MAX_FILE_COUNT = 5;
      const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB in bytes
      const CHUNK_THRESHOLD = 20 * 1024 * 1024; // 20MB threshold for chunking
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks (in base64 character length)

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum count
      if (currentFileCount + filesToProcess.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${filesToProcess.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${filesToProcess.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Calculate existing files total size
      let runningTotalSize = files.reduce((sum, fileObj) => {
        if (
          fileObj &&
          fileObj.isDelete === false &&
          fileObj.file &&
          typeof fileObj.file.size === "number"
        ) {
          return sum + fileObj.file.size;
        }
        return sum;
      }, 0);

      let successfulUploads = 0;

      // Process each file and stop when we hit the size limit
      for (let i = 0; i < filesToProcess.length; i++) {
        const file: any = filesToProcess[i];
        const fileId = `${file.name}-${Date.now()}-${i}`;

        // Check if adding this file would exceed the maximum count
        if (currentFileCount + successfulUploads >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break;
        }

        

        // Check individual file validity (excluding total size)
        const validationError = await validateFile(
          file,
          fileStatsRef.current.totalSize,
          fileStatsRef.current.count
        );

        if (validationError) {
          setError(`${validationError}`);
          setTimeout(() => setError(null), 3000);
          break;
        }

      
      

        // Set this file as uploading
        setUploadingFiles((prev: any) => ({
          ...prev,
          [fileId]: { isUploading: true, filename: file.name },
        }));

        try {
          // Convert file to base64
          const { file: uploadedFile, base64 } = await convertToBase64(file);
          // Increment the highest index for each new file
          highestIndexRef.current += 1;
          const newIndex = highestIndexRef.current;
          // Check if file needs chunking
          const needsChunking = uploadedFile.size > CHUNK_THRESHOLD;
          if (!needsChunking) {
            const fileData = {
              fileIndex: newIndex,
              fileId: 0,
              fileName: uploadedFile.name,
              mimeType: uploadedFile.type,
              isSensitive: false,
              media: base64,
              isDelete: false,
              sortOrder: 1, 
            };

            // Process the API call
            await new Promise((resolve, reject) => {
              handleFileApiOperation(fileData, batchId, "upload", {
                onSuccess: (res: any) => {
                  // Calculate new total size after successful upload
                  runningTotalSize += uploadedFile.size;

                  // Create file object
                  const fileWithSensitivity: FileWithSensitivity = {
                    file: uploadedFile,
                    isSensitive: false,
                    base64: base64,
                    systemCreatedDateTime: new Date(),
                    isNew: true,
                    isDelete: false,
                    fileFixedIndex: newIndex,
                  };

                  // Update files array
                  setFiles((prevFiles) => [...prevFiles, fileWithSensitivity]);
                  successfulUploads++;

                  setFilesCount((prevCount :any) => prevCount + 1);

                  // Remove from uploading state
                  setUploadingFiles((prev: any) => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                  });

                  fileStatsRef.current = {
                    count: fileStatsRef.current.count + 1,
                    totalSize: fileStatsRef.current.totalSize + uploadedFile.size
                  };

                  setSuccess(true);
                  setSuccessText(
                    `File ${uploadedFile.name} uploaded successfully.`
                  );
                  setTimeout(() => {
                    setSuccess(null);
                    setSuccessText("");
                  }, 3000);

                  resolve(res);
                },
                onError: (error: any) => {
                  console.log("API error during single upload:", error);
                  // Decrement the index since this file failed
                  highestIndexRef.current -= 1;

                  setUploadingFiles((prev: any) => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                  });

                  setFilesCount((prevCount : any) => prevCount - 1);

                  setError(
                    `Error uploading ${uploadedFile.name}: ${
                      error.message || "API call failed"
                    }`
                  );
                  setTimeout(() => setError(null), 3000);
                  reject(error);
                },
                onComplete: () => {
                  // Any cleanup needed
                },
              });
            });
          } else {
            console.log("Starting chunked upload for file:", uploadedFile.name);

            try {
              // Use the base64 directly - assume it has no prefix
              const base64Data = base64;

              // Calculate number of chunks needed
              const chunkSize = CHUNK_SIZE; // Adjust for base64 encoding overhead
              const totalChunks = Math.ceil(base64Data.length / chunkSize);
              console.log(`Splitting file into ${totalChunks} chunks`);

              let uploadedChunks = 0;
              let firstChunkResponse: any = null;

              // Process each chunk
              for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = Math.floor(chunkIndex * chunkSize);
                const end = Math.min(
                  Math.floor(start + chunkSize),
                  base64Data.length
                );
                const chunk = base64Data.substring(start, end);

                console.log(
                  `Processing chunk ${chunkIndex + 1}/${totalChunks}, size: ${
                    chunk.length
                  }`
                );

                // Prepare chunk data
                const chunkData = {
                  fileIndex: newIndex,
                  fileId: firstChunkResponse?.fileId || 0, // Use fileId from first chunk response for subsequent chunks
                  fileName: uploadedFile.name,
                  mimeType: uploadedFile.type,
                  isSensitive: false,
                  media: chunk,
                  isDelete: false,
                  sortOrder: chunkIndex + 1, // Start sortOrder from 1
                };

                // Process the API call for this chunk
                const res = await new Promise((resolve, reject) => {
                  handleFileApiOperation(chunkData, batchId, "upload", {
                    onSuccess: (response: any) => {
                      console.log(
                        `Chunk ${
                          chunkIndex + 1
                        }/${totalChunks} uploaded successfully`
                      );
                      uploadedChunks++;

                      // Save first chunk response to get fileId
                      if (chunkIndex === 0) {
                        firstChunkResponse = response;
                      }

                      resolve(response);
                    },
                    onError: (error: any) => {
                      console.log(
                        `Error uploading chunk ${
                          chunkIndex + 1
                        }/${totalChunks}:`,
                        error
                      );
                      reject(error);
                    },
                    onComplete: () => {
                      // Any cleanup needed
                    },
                  });
                });
              }

              console.log(`All ${totalChunks} chunks uploaded successfully`);

              // All chunks uploaded successfully
              if (uploadedChunks === totalChunks) {
                // Calculate new total size after successful upload
                runningTotalSize += uploadedFile.size;

                // Create file object (only storing first chunk in the state to save memory)
                const firstChunkOnly = base64Data.substring(
                  0,
                  Math.min(1000, base64Data.length)
                );
                const fileWithSensitivity: FileWithSensitivity = {
                  file: uploadedFile,
                  isSensitive: false,
                  base64: firstChunkOnly, // Store just a small part for preview
                  systemCreatedDateTime: new Date(),
                  isNew: true,
                  isDelete: false,
                  fileFixedIndex: newIndex,
                };

                // Update files array
                setFiles((prevFiles) => [...prevFiles, fileWithSensitivity]);
                successfulUploads++;

                fileStatsRef.current = {
                  count: fileStatsRef.current.count + 1,
                  totalSize: fileStatsRef.current.totalSize + uploadedFile.size
                };

                // Remove from uploading state
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                
                setSuccess(true);
                setSuccessText(
                  `File ${uploadedFile.name} uploaded successfully`
                );
                setTimeout(() => {
                  setSuccess(null);
                  setSuccessText("");
                }, 3000);
              } else {
                // Decrement the index since this file failed
                highestIndexRef.current -= 1;
                throw new Error(
                  `Only ${uploadedChunks}/${totalChunks} chunks were uploaded`
                );
              }
            } catch (chunkError) {
              console.log("Error during chunking process:", chunkError);
              // Decrement the index since this file failed
              highestIndexRef.current -= 1;
              throw chunkError; // Rethrow to be caught by outer try/catch
            }
          }

          // Check if we hit the size limit after a successful upload
          if (runningTotalSize >= MAX_TOTAL_SIZE) {
            if (i < filesToProcess.length - 1) {
              setError(
                `Files size would exceed 100MB limit. Uploaded ${successfulUploads} files.`
              );
              setTimeout(() => setError(null), 3000);
            }
            break; // Stop processing more files
          }
        } catch (err) {
          console.log("Error final catch:", err);
          // Clear loading state if any error occurs
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          setError(`Error processing ${file.name}`);
          setTimeout(() => setError(null), 3000);
        }
      }
    } catch (outerError) {
      console.log("Outer error in processFileUpload:", outerError);
    }
  };

  const handleFileApiOperation = async (
    fileData: any,
    batchId: any,
    operation: any,
    callbacks: {
      onStart?: () => void;
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
      onComplete?: () => void;
    }
  ): Promise<void> => {
    try {
      if (callbacks.onStart) callbacks.onStart();

      // Prepare request body according to the specified format
      const reqBody = {
        batchNumber: batchId,
        body: {
          ...fileData,
          // For delete operations, we set isDelete to true
          isDelete: operation === "delete",
        },
      };

      

      await uploadClientFileChunks(reqBody, {
        onSuccess: (res: any) => {
          if (callbacks.onSuccess) callbacks.onSuccess(res);
        },
        onError: (error: any) => {
          if (callbacks.onError) callbacks.onError(error);
          throw new Error(`API ${operation} failed`);
        },
      });
    } catch (error) {
      if (callbacks.onError) callbacks.onError(error);
    } finally {
      if (callbacks.onComplete) callbacks.onComplete();
    }
  };

  const handleFileUploadSingle = async (
    selectedFiles: any | null
  ): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    try {
      const MAX_FILE_COUNT = 5;

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum
      if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${selectedFiles.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${selectedFiles.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Track how many files we've successfully added
      let successfullyAddedCount = 0;

      // Process each file sequentially
      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles[i];
        const fileId: string = `${file.name}-${Date.now()}`;

        // Check if adding this file would exceed the maximum (including files added in this loop)
        if (currentFileCount + successfullyAddedCount >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break; // Stop processing more files
        }

        // Calculate current total size
        const currentFiles = [...files];
        let totalSize = currentFiles.reduce((sum, fileObj) => {
          if (
            fileObj &&
            fileObj.isDelete === false &&
            fileObj.file &&
            typeof fileObj.file.size === "number"
          ) {
            return sum + fileObj.file.size;
          }
          return sum;
        }, 0);

        // Validation...
        const validationError = validateFile(
          file,
          totalSize,
          currentFileCount + successfullyAddedCount
        );

        if (validationError) {
          setError(validationError);
          setTimeout(() => setError(null), 3000);
          continue; // Skip this file and continue with next
        }

        try {
          // Set file as uploading before converting to base64
          setUploadingFiles((prev: any) => ({
            ...prev,
            [fileId]: { isUploading: true, filename: file.name },
          }));

          // Convert file to base64
          const { file: uploadedFile, base64 } = await convertToBase64(file);

          const newIndex = files.length + 1;

          // Prepare file data
          const fileData = {
            fileIndex: newIndex,
            fileId: 0,
            fileName: uploadedFile.name,
            mimeType: uploadedFile.type,
            isSensitive: false,
            media: base64,
            isDelete: false,
            sortOrder: 0,
          };

          // Use the new API function for upload
          await handleFileApiOperation(fileData, batchId, "upload", {
            onSuccess: (res: any) => {
              // Clear loading state after successful API call
              setUploadingFiles((prev: any) => {
                const newState = { ...prev };
                delete newState[fileId];
                return newState;
              });

              // Handle successful upload
              const fileWithSensitivity: FileWithSensitivity = {
                file: uploadedFile,
                isSensitive: false,
                base64: base64,
                systemCreatedDateTime: new Date(),
                isNew: true,
                isDelete: false,
                fileFixedIndex: newIndex,
              };

              setFiles((prevFiles: any) => {
                const currentCount = prevFiles.filter(
                  (f: any) => f && f.isDelete === false
                ).length;
                if (currentCount >= MAX_FILE_COUNT) {
                  setError(
                    `Maximum number of files (${MAX_FILE_COUNT}) reached.`
                  );
                  setTimeout(() => setError(null), 3000);
                  return prevFiles;
                }
                return [...prevFiles, fileWithSensitivity];
              });

              successfullyAddedCount++;

              setSuccess(true);
              setSuccessText("File uploaded successfully");
              setTimeout(() => {
                setSuccess(null);
                setSuccessText("");
              }, 3000);
            },
            onError: (error: any) => {
              // Clear loading state on error
              setUploadingFiles((prev: any) => {
                const newState = { ...prev };
                delete newState[fileId];
                return newState;
              });

              setError(`Error uploading ${uploadedFile.name}: API call failed`);
              setTimeout(() => setError(null), 3000);
            },
            onComplete: () => {
              // Any cleanup that needs to happen regardless of success/failure
            },
          });
        } catch (err) {
          // Clear loading state if any error occurs
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          setError(`Error processing ${file.name}`);
          setTimeout(() => setError(null), 3000);
          setSuccess(false);
          setSuccessText("");
          // Break the loop on processing error
          break;
        }
      }
    } finally {
      // Clean up if needed
    }
  };

  const handleFileUpload123 = async (
    selectedFiles: any | null
  ): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    try {
      const MAX_FILE_COUNT = 5;
      const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB in bytes

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum count
      if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${selectedFiles.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${selectedFiles.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Calculate existing files total size
      let runningTotalSize = files.reduce((sum, fileObj) => {
        if (
          fileObj &&
          fileObj.isDelete === false &&
          fileObj.file &&
          typeof fileObj.file.size === "number"
        ) {
          return sum + fileObj.file.size;
        }
        return sum;
      }, 0);

      // Get highest current index
      const getHighestIndex = () => {
        return files.reduce((highest, file) => {
          if (file && typeof file.fileFixedIndex === "number") {
            return Math.max(highest, file.fileFixedIndex);
          }
          return highest;
        }, 0);
      };

      const highestCurrentIndex = getHighestIndex();
      let successfulUploads = 0;
      let filesToProcess = Array.from(selectedFiles);

      // Process each file and stop when we hit the size limit
      for (let i = 0; i < filesToProcess.length; i++) {
        const file: any = filesToProcess[i];
        const fileId = `${file.name}-${Date.now()}-${i}`;

        // Check if adding this file would exceed the maximum count
        if (currentFileCount + successfulUploads >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break;
        }

        // Check individual file validity (excluding total size)
        const validationError = validateFile(
          file,
          runningTotalSize,
          currentFileCount + successfulUploads
        );

        if (validationError) {
          setError(`${file.name}: ${validationError}`);
          setTimeout(() => setError(null), 3000);
          continue;
        }

        // Set this file as uploading
        setUploadingFiles((prev: any) => ({
          ...prev,
          [fileId]: { isUploading: true, filename: file.name },
        }));

        try {
          // Convert file to base64
          const { file: uploadedFile, base64 } = await convertToBase64(file);

          // Calculate new index for this file
          const newIndex = highestCurrentIndex + successfulUploads + 1;


          // Prepare file data
          const fileData = {
            fileIndex: newIndex,
            fileId: 0,
            fileName: uploadedFile.name,
            mimeType: uploadedFile.type,
            isSensitive: false,
            media: base64,
            isDelete: false,
            sortOrder: 0,
          };

          // Process the API call
          await new Promise((resolve, reject) => {
            handleFileApiOperation(fileData, batchId, "upload", {
              onSuccess: (res: any) => {
                // Calculate new total size after successful upload
                runningTotalSize += uploadedFile.size;

                // Create file object
                const fileWithSensitivity: FileWithSensitivity = {
                  file: uploadedFile,
                  isSensitive: false,
                  base64: base64,
                  systemCreatedDateTime: new Date(),
                  isNew: true,
                  isDelete: false,
                  fileFixedIndex: newIndex,
                };

                // Update files array
                setFiles((prevFiles) => [...prevFiles, fileWithSensitivity]);
                successfulUploads++;

                // Remove from uploading state
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                setSuccess(true);
                setSuccessText(
                  `File ${uploadedFile.name} uploaded successfully`
                );
                setTimeout(() => {
                  setSuccess(null);
                  setSuccessText("");
                }, 3000);

                resolve(res);
              },
              onError: (error: any) => {
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                setError(
                  `Error uploading ${uploadedFile.name}: ${
                    error.message || "API call failed"
                  }`
                );
                setTimeout(() => setError(null), 3000);
                reject(error);
              },
              onComplete: () => {
                // Any cleanup needed
              },
            });
          });

          // Check if we hit the size limit after a successful upload
          if (runningTotalSize >= MAX_TOTAL_SIZE) {
            if (i < filesToProcess.length - 1) {
              setError(
                `Files size would exceed 100MB limit. Uploaded ${successfulUploads} files.`
              );
              setTimeout(() => setError(null), 3000);
            }
            break; // Stop processing more files
          }
        } catch (err) {
          // Clear loading state if any error occurs
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          setError(`Error processing ${file.name}`);
          setTimeout(() => setError(null), 3000);
        }
      }
    } finally {
      // Clean up if needed
    }
  };

  const handleFileUploadWithChunks = async (
    selectedFiles: any | null
  ): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    try {
      const MAX_FILE_COUNT = 5;
      const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB in bytes
      const CHUNK_THRESHOLD = 20 * 1024 * 1024; // 20MB threshold for chunking
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks (in base64 character length)

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum count
      if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${selectedFiles.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${selectedFiles.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Calculate existing files total size
      let runningTotalSize = files.reduce((sum, fileObj) => {
        if (
          fileObj &&
          fileObj.isDelete === false &&
          fileObj.file &&
          typeof fileObj.file.size === "number"
        ) {
          return sum + fileObj.file.size;
        }
        return sum;
      }, 0);

      // Get highest current index
      const getHighestIndex = () => {
        return files.reduce((highest, file) => {
          if (file && typeof file.fileFixedIndex === "number") {
            return Math.max(highest, file.fileFixedIndex);
          }
          return highest;
        }, 0);
      };

      const highestCurrentIndex = getHighestIndex();
      let successfulUploads = 0;
      let filesToProcess = Array.from(selectedFiles);

      // Process each file and stop when we hit the size limit
      for (let i = 0; i < filesToProcess.length; i++) {
        const file: any = filesToProcess[i];
        const fileId = `${file.name}-${Date.now()}-${i}`;

        // Check if adding this file would exceed the maximum count
        if (currentFileCount + successfulUploads >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break;
        }

        // Check individual file validity (excluding total size)
        const validationError = validateFile(
          file,
          runningTotalSize,
          currentFileCount + successfulUploads
        );

        if (validationError) {
          setError(`${file.name}: ${validationError}`);
          setTimeout(() => setError(null), 3000);
          continue;
        }

        // Set this file as uploading
        setUploadingFiles((prev: any) => ({
          ...prev,
          [fileId]: { isUploading: true, filename: file.name },
        }));

        try {
          // Convert file to base64
          const { file: uploadedFile, base64 } = await convertToBase64(file);
          console.log(
            "File converted to base64:",
            uploadedFile.name,
            "Size:",
            uploadedFile.size
          );

          // Calculate new index for this file
          const newIndex = highestCurrentIndex + successfulUploads + 1;

          // Check if file needs chunking
          const needsChunking = uploadedFile.size > CHUNK_THRESHOLD;

          if (!needsChunking) {
            console.log("Single upload for file:", uploadedFile.name);
            // Regular single upload process for smaller files
            const fileData = {
              fileIndex: newIndex,
              fileId: 0,
              fileName: uploadedFile.name,
              mimeType: uploadedFile.type,
              isSensitive: false,
              media: base64,
              isDelete: false,
              sortOrder: 1, // Single file gets sortOrder 1
            };

            // Process the API call
            await new Promise((resolve, reject) => {
              handleFileApiOperation(fileData, batchId, "upload", {
                onSuccess: (res: any) => {
                  // Calculate new total size after successful upload
                  runningTotalSize += uploadedFile.size;

                  // Create file object
                  const fileWithSensitivity: FileWithSensitivity = {
                    file: uploadedFile,
                    isSensitive: false,
                    base64: base64,
                    systemCreatedDateTime: new Date(),
                    isNew: true,
                    isDelete: false,
                    fileFixedIndex: newIndex,
                  };

                  // Update files array
                  setFiles((prevFiles) => [...prevFiles, fileWithSensitivity]);
                  successfulUploads++;

                  // Remove from uploading state
                  setUploadingFiles((prev: any) => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                  });

                  setSuccess(true);
                  setSuccessText(
                    `File ${uploadedFile.name} uploaded successfully`
                  );
                  setTimeout(() => {
                    setSuccess(null);
                    setSuccessText("");
                  }, 3000);

                  resolve(res);
                },
                onError: (error: any) => {
                  console.log("API error during single upload:", error);
                  setUploadingFiles((prev: any) => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                  });

                  setError(
                    `Error uploading ${uploadedFile.name}: ${
                      error.message || "API call failed"
                    }`
                  );
                  setTimeout(() => setError(null), 3000);
                  reject(error);
                },
                onComplete: () => {
                  // Any cleanup needed
                },
              });
            });
          } else {
            console.log("Starting chunked upload for file:", uploadedFile.name);

            try {
              // Use the base64 directly - assume it has no prefix
              const base64Data = base64;

              // Calculate number of chunks needed
              const chunkSize = CHUNK_SIZE * 0.75; // Adjust for base64 encoding overhead
              const totalChunks = Math.ceil(base64Data.length / chunkSize);
              console.log(`Splitting file into ${totalChunks} chunks`);

              let uploadedChunks = 0;
              let firstChunkResponse: any = null;

              // Process each chunk
              for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = Math.floor(chunkIndex * chunkSize);
                const end = Math.min(
                  Math.floor(start + chunkSize),
                  base64Data.length
                );
                const chunk = base64Data.substring(start, end);

                console.log(
                  `Processing chunk ${chunkIndex + 1}/${totalChunks}, size: ${
                    chunk.length
                  }`
                );

                // Prepare chunk data
                const chunkData = {
                  fileIndex: newIndex,
                  fileId: firstChunkResponse?.fileId || 0, // Use fileId from first chunk response for subsequent chunks
                  fileName: uploadedFile.name,
                  mimeType: uploadedFile.type,
                  isSensitive: false,
                  media: chunk,
                  isDelete: false,
                  sortOrder: chunkIndex + 1, // Start sortOrder from 1
                };

                // Process the API call for this chunk
                const res = await new Promise((resolve, reject) => {
                  handleFileApiOperation(chunkData, batchId, "upload", {
                    onSuccess: (response: any) => {
                      console.log(
                        `Chunk ${
                          chunkIndex + 1
                        }/${totalChunks} uploaded successfully`
                      );
                      uploadedChunks++;

                      // Save first chunk response to get fileId
                      if (chunkIndex === 0) {
                        firstChunkResponse = response;
                      }

                      resolve(response);
                    },
                    onError: (error: any) => {
                      console.log(
                        `Error uploading chunk ${
                          chunkIndex + 1
                        }/${totalChunks}:`,
                        error
                      );
                      reject(error);
                    },
                    onComplete: () => {
                      // Any cleanup needed
                    },
                  });
                });
              }

              console.log(`All ${totalChunks} chunks uploaded successfully`);

              // All chunks uploaded successfully
              if (uploadedChunks === totalChunks) {
                // Calculate new total size after successful upload
                runningTotalSize += uploadedFile.size;

                // Create file object (only storing first chunk in the state to save memory)
                const firstChunkOnly = base64Data.substring(
                  0,
                  Math.min(1000, base64Data.length)
                );
                const fileWithSensitivity: FileWithSensitivity = {
                  file: uploadedFile,
                  isSensitive: false,
                  base64: firstChunkOnly, // Store just a small part for preview
                  systemCreatedDateTime: new Date(),
                  isNew: true,
                  isDelete: false,
                  fileFixedIndex: newIndex,
                };

                // Update files array
                setFiles((prevFiles) => [...prevFiles, fileWithSensitivity]);
                successfulUploads++;

                // Remove from uploading state
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                setSuccess(true);
                setSuccessText(
                  `File ${uploadedFile.name} uploaded successfully in ${totalChunks} chunks`
                );
                setTimeout(() => {
                  setSuccess(null);
                  setSuccessText("");
                }, 3000);
              } else {
                throw new Error(
                  `Only ${uploadedChunks}/${totalChunks} chunks were uploaded`
                );
              }
            } catch (chunkError) {
              console.log("Error during chunking process:", chunkError);
              throw chunkError; // Rethrow to be caught by outer try/catch
            }
          }

          // Check if we hit the size limit after a successful upload
          if (runningTotalSize >= MAX_TOTAL_SIZE) {
            if (i < filesToProcess.length - 1) {
              setError(
                `Files size would exceed 100MB limit. Uploaded ${successfulUploads} files.`
              );
              setTimeout(() => setError(null), 3000);
            }
            break; // Stop processing more files
          }
        } catch (err) {
          console.log("Error final catch:", err);
          // Clear loading state if any error occurs
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          setError(`Error processing ${file.name}`);
          setTimeout(() => setError(null), 3000);
        }
      }
    } catch (outerError) {
      console.log("Outer error in handleFileUpload:", outerError);
    } finally {
      // Clean up if needed
    }
  };

  const [deletingFiles, setDeletingFiles] = useState<Record<number, boolean>>(
    {}
  );

  const handleFileDelete1 = async (
    fileToDelete: any,
    fileIndex: any
  ): Promise<void> => {
    console.log("handleFileDelete", fileToDelete, fileIndex);
    try {
      if (!fileToDelete) return;

      // Set loading for this specific file index only
      setDeletingFiles((prev) => ({ ...prev, [fileIndex]: true }));

      const fileData = {
        fileIndex: fileToDelete?.fileFixedIndex,
        fileId: fileToDelete.id || 0,
        fileName: fileToDelete.file?.name,
        mimeType: fileToDelete.file?.type,
        media: fileToDelete?.base64 || "",
        isSensitive: fileToDelete.isSensitive,
        isDelete: true,
        sortOrder: 0,
      };

      await handleFileApiOperation(fileData, batchId, "delete", {
        onStart: () => {
          // Set loading state for deletion if needed
        },
        onSuccess: (res: any) => {
          setFiles((prevFiles: any) =>
            prevFiles.filter((_: any, index: any) => index !== fileIndex)
          );
          // Clear loading state for this file
          setDeletingFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          setSuccess(true);
          setSuccessText("File deleted successfully");
          setTimeout(() => {
            setSuccess(null);
            setSuccessText("");
          }, 3000);
        },
        onError: (error: any) => {
          // Clear loading state for this file
          setDeletingFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          setError(`Error deleting ${fileToDelete.file?.name}`);
          setTimeout(() => setError(null), 3000);
        },
        onComplete: () => {
          // Any cleanup that needs to happen regardless of success/failure
        },
      });
    } catch (error) {
      // Clear loading state for this file
      setDeletingFiles((prev) => {
        const newState = { ...prev };
        delete newState[fileIndex];
        return newState;
      });
      setError(
        `Error deleting file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleFileDelete = async (
    fileToDelete: any,
    fileIndex: any
  ): Promise<void> => {
    console.log("handleFileDelete", fileToDelete, fileIndex);
    try {
      if (!fileToDelete) return;
  
      // Set loading for this specific file index only
      setDeletingFiles((prev) => ({ ...prev, [fileIndex]: true }));
  
      const fileData = {
        fileIndex: fileToDelete?.fileFixedIndex,
        fileId: fileToDelete.id || 0,
        fileName: fileToDelete.file?.name,
        mimeType: fileToDelete.file?.type,
        media: fileToDelete?.base64 || "",
        isSensitive: fileToDelete.isSensitive,
        isDelete: true,
        sortOrder: 0,
      };
  
      await handleFileApiOperation(fileData, batchId, "delete", {
        onStart: () => {
          // Set loading state for deletion if needed
        },
        onSuccess: (res: any) => {

          fileStatsRef.current = {
            count: fileStatsRef.current.count - 1,
            totalSize: fileStatsRef.current.totalSize - (fileToDelete.file?.size || 0)
          };

          
          // Mark the file as deleted instead of removing it completely
          setFiles((prevFiles: any) =>
            prevFiles.map((file: any, index: any) => {
              if (index === fileIndex) {
                return { ...file, isDelete: true };
              }
              return file;
            })
          );
          
          // Clear loading state for this file
          setDeletingFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          setSuccess(true);
          setSuccessText("File deleted successfully");
          setTimeout(() => {
            setSuccess(null);
            setSuccessText("");
          }, 3000);
        },
        onError: (error: any) => {
          // Clear loading state for this file
          setDeletingFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          setError(`Error deleting ${fileToDelete.file?.name}`);
          setTimeout(() => setError(null), 3000);
        },
        onComplete: () => {
          // Any cleanup that needs to happen regardless of success/failure
        },
      });
    } catch (error) {
      // Clear loading state for this file
      setDeletingFiles((prev) => {
        const newState = { ...prev };
        delete newState[fileIndex];
        return newState;
      });
      setError(
        `Error deleting file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleFileUpload2 = async (
    selectedFiles: any | null
  ): Promise<void> => {
    if (!selectedFiles || selectedFiles?.length === 0) return;

    try {
      const MAX_FILE_COUNT = 5;

      // Get the most current file count from the state (not deleted files)
      const currentFileCount = files.filter(
        (file) => file && file.isDelete === false
      ).length;

      // Check if adding new files would exceed the maximum
      if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
        if (currentFileCount === 0) {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You're trying to upload ${selectedFiles.length} files.`
          );
        } else {
          setError(
            `Maximum number of files is ${MAX_FILE_COUNT}. You already have ${currentFileCount} file(s) and are trying to add ${selectedFiles.length} more.`
          );
        }
        setTimeout(() => setError(null), 3000);
        return; // Exit completely if we would exceed the limit
      }

      // Track how many files we've successfully added
      let successfullyAddedCount = 0;

      // Process each file sequentially
      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles[i];
        const fileId: string = `${file.name}-${Date.now()}`;

        // Check if adding this file would exceed the maximum (including files added in this loop)
        if (currentFileCount + successfullyAddedCount >= MAX_FILE_COUNT) {
          setError(`Maximum number of files (${MAX_FILE_COUNT}) reached.`);
          setTimeout(() => setError(null), 3000);
          break; // Stop processing more files
        }

        // Calculate current total size
        const currentFiles = [...files];
        let totalSize = currentFiles.reduce((sum, fileObj) => {
          if (
            fileObj &&
            fileObj.isDelete === false &&
            fileObj.file &&
            typeof fileObj.file.size === "number"
          ) {
            return sum + fileObj.file.size;
          }
          return sum;
        }, 0);

        // Validation...
        const validationError = validateFile(
          file,
          totalSize,
          currentFileCount + successfullyAddedCount
        );

        if (validationError) {
          setError(validationError);
          setTimeout(() => setError(null), 3000);
          continue; // Skip this file and continue with next
        }

        try {
          // Set file as uploading before converting to base64
          setUploadingFiles((prev: any) => ({
            ...prev,
            [fileId]: { isUploading: true, filename: file.name },
          }));

          // Convert file to base64
          const { file: uploadedFile, base64 } = await convertToBase64(file);

          // Prepare request body according to the specified format
          const fileData = {
            fileIndex: currentFileCount + successfullyAddedCount,
            fileId: 0,
            fileName: uploadedFile.name,
            mimeType: uploadedFile.type,
            isSensitive: false,
            media: base64,
            isDelete: false,
            sortOrder: 0,
          };

          // API call configuration with batch ID in header
          const reqBody = {
            batchNumber: batchId,
            body: fileData,
          };

          // Call the API for this file - keep loading state during API call
          console.log("reqBody", reqBody);

          try {
            await uploadClientFileChunks(reqBody, {
              onSuccess: (res: any) => {
                console.log("uploadClientFileChunks onSuccess", res);
                // Clear loading state after successful API call
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                // Handle successful upload
                const fileWithSensitivity: FileWithSensitivity = {
                  file: uploadedFile,
                  isSensitive: false,
                  base64: base64,
                  systemCreatedDateTime: new Date(),
                  isNew: true,
                  isDelete: false,
                };

                setFiles((prevFiles: any) => {
                  const currentCount = prevFiles.filter(
                    (f: any) => f && f.isDelete === false
                  ).length;
                  if (currentCount >= MAX_FILE_COUNT) {
                    setError(
                      `Maximum number of files (${MAX_FILE_COUNT}) reached.`
                    );
                    setTimeout(() => setError(null), 3000);
                    return prevFiles;
                  }
                  return [...prevFiles, fileWithSensitivity];
                });

                successfullyAddedCount++;

                setSuccess(true);
                setTimeout(() => setSuccess(null), 3000);
              },
              onError: (error: any) => {
                // Clear loading state on error
                console.log("uploadClientFileChunks onError", error);
                setUploadingFiles((prev: any) => {
                  const newState = { ...prev };
                  delete newState[fileId];
                  return newState;
                });

                setError(
                  `Error uploading ${uploadedFile.name}: API call failed`
                );
                setTimeout(() => setError(null), 3000);
                // Breaking the loop when API call fails
                throw new Error("API upload failed");
              },
            });
          } catch (apiError) {
            console.log("uploadClientFileChunks apiError", apiError);
            // Clear loading state if API call throws
            setUploadingFiles((prev: any) => {
              const newState = { ...prev };
              delete newState[fileId];
              return newState;
            });

            // If API call fails, exit the loop
            setError(
              `Upload process stopped due to error with ${uploadedFile.name}`
            );
            setTimeout(() => setError(null), 3000);
            break;
          }
        } catch (err) {
          // Clear loading state if any error occurs
          setUploadingFiles((prev: any) => {
            const newState = { ...prev };
            delete newState[fileId];
            return newState;
          });

          setError(`Error processing ${file.name}`);
          setTimeout(() => setError(null), 3000);
          setSuccess(false);
          setSuccessText("");
          // Break the loop on processing error
          break;
        }
      }
    } finally {
      // Clean up if needed
    }
  };

  // Handle input change
  const handleInputChange = (e: any): void => {
    console.log("e.target.files", e.target.files);
    const files = Array.from(e.target.files);
    handleFileUpload(files);
    // Reset input value so the same file can be selected again
    e.target.value = "";
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 KB"; // Handle zero bytes case
    const kilobytes = bytes / 1024; // Convert bytes to kilobytes
    return `${kilobytes.toFixed(2)} KB`; // Format to 2 decimal places and append "KB"
  };

  const getFileUploadDate = (file: any): string => {
    const date = new Date(file?.systemCreatedDateTime);

    const formattedDate = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Use 12-hour format
    });

    return `${formattedDate} at ${formattedTime}`;
  };

  const removeFile = (uiIndex: number, file: any): void => {
    setFiles((prev) =>
      prev.map((f, i) => (i === uiIndex ? { ...f, isDelete: true } : f))
    );
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault(); // This is crucial for Firefox
    e.dataTransfer.dropEffect = "copy"; // Explicitly set the drop effect
  };

  const handleDrop = (e: any): void => {
    e.preventDefault();
    console.log("Files dropped:");
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
    e.target.value = "";
  };

  const toggleSensitivity = (index: number, file: any): void => {
    const actualIndex = files.findIndex((f) => f === file);

    setFiles((prev: any) =>
      prev.map((file: any, i: number) =>
        i === index ? { ...file, isSensitive: !file.isSensitive } : file
      )
    );
  };

  const [changingStatusFiles, setChangingStatusFiles] = useState<
    Record<number, boolean>
  >({});

  const handleToggleSensitivity = async (
    file: any,
    fileIndex: any
  ): Promise<void> => {
    console.log("handleToggleSensitivity", file, fileIndex);
    try {
      if (!file) return;

      const fileData = {
        fileIndex: file?.fileFixedIndex,
        fileId: file.id || 0,
        fileName: file.file?.name,
        mimeType: file.file?.type,
        media: file?.base64 || "",
        isSensitive: !file.isSensitive,
        isDelete: false,
        sortOrder: 0,
      };

      // Set loading for this specific file index only
      setChangingStatusFiles((prev) => ({ ...prev, [fileIndex]: true }));

      await handleFileApiOperation(fileData, batchId, "status", {
        onStart: () => {
          // Set loading state for status change if needed
        },
        onSuccess: (res: any) => {
          setFiles((prev: any) =>
            prev.map((file: any, i: number) =>
              i === fileIndex
                ? { ...file, isSensitive: !file.isSensitive }
                : file
            )
          );
          // Clear loading state for this file
          setChangingStatusFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          const status = file.isSensitive ? "Non-Sensitive" : "Sensitive";
          const statusChangeSuccessMessage = `${file.file?.name} has been changed to ${status}`;
          setSuccess(true);
          setSuccessText(statusChangeSuccessMessage);
          setTimeout(() => {
            setSuccess(null);
            setSuccessText("");
          }, 3000);
        },
        onError: (error: any) => {
          // Clear loading state for this file
          setChangingStatusFiles((prev) => {
            const newState = { ...prev };
            delete newState[fileIndex];
            return newState;
          });
          setError(`Error Updating Status ${file.file?.name}`);
          setTimeout(() => setError(null), 3000);
        },
        onComplete: () => {
          // Any cleanup that needs to happen regardless of success/failure
        },
      });
    } catch (error) {
      // Clear loading state for this file
      setChangingStatusFiles((prev) => {
        const newState = { ...prev };
        delete newState[fileIndex];
        return newState;
      });
      setError(
        `Error Updating Status of file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setTimeout(() => setError(null), 3000);
    }
  };

  const isUploading = Object.keys(uploadingFiles)?.length > 0;

  const isSubmitEnabledFileUpload = (dirty: any, values: any) => {
    if (!values.client?.label) {
      return false;
    }
    if (!files || files.length === 0 || files.every((file) => file.isDelete)) {
      return false;
    }

    const newUndeletedFiles = files.filter(
      (file) => file.isNew && !file.isDelete
    );
    const deletedExistingFiles = files.filter(
      (file) => !file.isNew && file.isDelete
    );

    return (
      files.some((file) => !file.isDelete) &&
      (newUndeletedFiles.length > 0 || deletedExistingFiles.length > 0)
    );
  };

  const isSubmitEnabledFormUpload = (
    isValid: boolean,
    touched: any,
    dirty: boolean,
    errors: any,
    values: any
  ) => {
    const newUndeletedFiles = files.filter(
      (file) => file.isNew && !file.isDelete
    );
    const deletedExistingFiles = files.filter(
      (file) => !file.isNew && file.isDelete
    );

    const isFormTouched = !isEditMode ? Object.keys(touched).length > 0 : true;
    const hasNoErrors = Object.keys(errors).length === 0;
    const hasFileChanges =
      newUndeletedFiles.length > 0 || deletedExistingFiles.length > 0;

    const hasWhitespaceOnlyValues = (values: any) => {
      // Check text fields that should not be just whitespace
      const fieldsToCheck = [
        "title",
        "hypothesis",
        "background",
        "testStoresOrGeography",
        "controlStoresOrGeography",
        "testDesign",
        "results",
      ];

      return fieldsToCheck.some((field) => {
        return (
          typeof values[field] === "string" &&
          values[field].trim().length === 0 &&
          values[field].length > 0
        );
      });
    };

    const isSubmitEnabled =
      isValid &&
      isFormTouched &&
      hasNoErrors &&
      (dirty || hasFileChanges) &&
      !hasWhitespaceOnlyValues(values);

    return isSubmitEnabled;
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize
        validationSchema={Yup.object(getValidationSchema())}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values) => {
          console.log("onSubmit values: files", files, id);
          setLoader(true);
          handleSave(files, { ...values }, id, batchId);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          setFieldTouched,
          isValid,
          dirty,
        }) => {
          return (
            <Form>
              <DialogContent
                dividers
                sx={{
                  maxHeight: "calc(100vh - 235px)",
                  minHeight: "150px !important",
                }}
              >
                <Grid container spacing={0} sx={{ p: "10px" }}>
                  <Grid container sx={{ mb: 1 }}>
                    <Grid sm={12} md={6} item>
                      <Autocomplete
                        options={clients}
                        getOptionLabel={(option: any) => option.label}
                        value={values.client.label ? values.client : null}
                        disabled={isEditMode}
                        onChange={(event, newValue) => {
                          if (newValue === null) {
                            setFieldValue("client", { label: "", value: "" });
                            setTimeout(() => {
                              setFieldTouched("client", true, true);
                            }, 10);
                          } else {
                            setFieldValue("client", newValue);
                            setFieldTouched("client", true, true);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Client"
                            variant="outlined"
                            error={
                              (touched.client && !values.client.label) ||
                              (touched?.client?.label &&
                                Boolean(errors?.client?.label))
                            }
                            helperText={
                              touched.client && !values.client.label ? (
                                <span>
                                  Required
                                  <span className="text-red">*</span>
                                </span>
                              ) : (
                                touched?.client?.label && errors?.client?.label
                              )
                            }
                            onBlur={() => setFieldTouched("client", true, true)}
                          />
                        )}
                        clearIcon={values.client.label ? undefined : null}
                      />
                    </Grid>
                  </Grid>

                  {/* research and exp*/}
                  {uploadType == "research" && (
                    <>
                      <Grid sm={12} md={6} item sx={{ mb: 1 }}>
                        <Autocomplete
                          id="testAndLearnVehicle"
                          options={vehicleOptions}
                          value={values.testAndLearnVehicle}
                          onChange={(event, newValue) => {
                            setFieldValue("testAndLearnVehicle", newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="testAndLearnVehicle"
                              label="Test and Learn Vehicle(s)"
                              error={
                                touched.testAndLearnVehicle &&
                                Boolean(errors.testAndLearnVehicle)
                              }
                              helperText={
                                <span>
                                  Required
                                  <span className="text-red">*</span>
                                </span>
                              }
                              onBlur={handleBlur}
                            />
                          )}
                          clearIcon={
                            values.testAndLearnVehicle ? undefined : null
                          }
                        />
                      </Grid>
                      {/* Title */}
                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="title"
                            name="title"
                            label="Title"
                            value={values.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.title && Boolean(errors.title)}
                            //helperText={touched.title && errors.title}
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* Hypothesis */}
                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="hypothesis"
                            name="hypothesis"
                            label="Hypothesis"
                            value={values.hypothesis}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.hypothesis && Boolean(errors.hypothesis)
                            }
                            // helperText={touched.hypothesis && errors.hypothesis}
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* Background */}
                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="background"
                            name="background"
                            label="Background"
                            value={values.background}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.background && Boolean(errors.background)
                            }
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                            // InputLabelProps={{
                            //   shrink: true,
                            // }}
                          />
                        </Grid>
                      </Grid>

                      {/* Test Stores */}
                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="testStoresOrGeography"
                            name="testStoresOrGeography"
                            label="Test Stores Or Geography"
                            value={values.testStoresOrGeography}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.testStoresOrGeography &&
                              Boolean(errors.testStoresOrGeography)
                            }
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                            // InputLabelProps={{
                            //   shrink: true,
                            // }}
                          />
                        </Grid>
                      </Grid>

                      {/* Control Stores */}
                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="controlStoresOrGeography"
                            value={values.controlStoresOrGeography}
                            name="controlStoresOrGeography"
                            label="Control Stores Or Geography"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.controlStoresOrGeography &&
                              Boolean(errors.controlStoresOrGeography)
                            }
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                            // InputLabelProps={{
                            //   shrink: true,
                            // }}
                          />
                        </Grid>
                      </Grid>

                      {/* Measured KPI */}
                      <Grid sm={12} md={6} item sx={{ mb: 1 }}>
                        <Grid container spacing={2.5}>
                          <Grid xs={6} item>
                            <Autocomplete
                              id="measuredKPI"
                              options={kpiOptions}
                              value={values.measuredKPI}
                              onChange={(event, newValue) => {
                                setFieldValue("measuredKPI", newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="measuredKPI"
                                  label="Measured KPI"
                                  error={
                                    touched.measuredKPI &&
                                    Boolean(errors.measuredKPI)
                                  }
                                  helperText={
                                    <span>
                                      Required
                                      <span className="text-red">*</span>
                                    </span>
                                  }
                                  onBlur={handleBlur}
                                />
                              )}
                              clearIcon={values.measuredKPI ? undefined : null}
                              // freeSolo
                            />
                          </Grid>

                          <Grid xs={6} item>
                            <TextField
                              fullWidth
                              id="testDesign"
                              name="testDesign"
                              label="Test Design"
                              value={values.testDesign}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.testDesign && Boolean(errors.testDesign)
                              }
                              helperText={
                                <span>
                                  Required<span className="text-red">*</span>
                                </span>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid container spacing={0} sx={{ mb: 1 }}>
                        <Grid sm={12} md={6}>
                          <Grid container spacing={2.5}>
                            <Grid xs={12} sm={6} item>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer
                                  components={["DatePicker", "DatePicker"]}
                                >
                                  <DatePicker
                                    label="Test Start Date"
                                    value={values.testStartDate}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={(newValue) => {
                                      // Ensure we're storing a valid dayjs object
                                      if (
                                        newValue &&
                                        dayjs(newValue).isValid()
                                      ) {
                                        console.log(
                                          "newValue",
                                          dayjs(newValue)
                                        );
                                        const parsedDate = dayjs(newValue);
                                        console.log(
                                          "parsedDate",
                                          parsedDate.format("MM/DD/YYYY")
                                        );
                                        setFieldValue(
                                          "testStartDate",
                                          dayjs(newValue)
                                        );
                                        console.log(
                                          "errors.testEndDate",
                                          errors.testEndDate
                                        );
                                        if (errors.testEndDate) {
                                          setTimeout(
                                            () =>
                                              setFieldTouched(
                                                "testEndDate",
                                                true,
                                                true
                                              ),
                                            10
                                          );
                                        }
                                        // Run validation after value is set
                                        setTimeout(
                                          () =>
                                            setFieldTouched(
                                              "testStartDate",
                                              true,
                                              true
                                            ),
                                          10
                                        );
                                      } else {
                                        setFieldValue("testStartDate", null);
                                        setFieldTouched(
                                          "testStartDate",
                                          true,
                                          true
                                        );
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        error:
                                          touched.testStartDate &&
                                          Boolean(errors.testStartDate),
                                        helperText:
                                          touched.testStartDate &&
                                          errors.testStartDate ? (
                                            errors.testStartDate
                                          ) : (
                                            <span>
                                              Required
                                              <span className="text-red">
                                                *
                                              </span>
                                            </span>
                                          ),
                                        onBlur: () =>
                                          setFieldTouched(
                                            "testStartDate",
                                            true,
                                            true
                                          ),
                                      },
                                    }}
                                  />
                                </DemoContainer>
                              </LocalizationProvider>
                            </Grid>

                            <Grid xs={12} sm={6} item>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer
                                  components={["DatePicker", "DatePicker"]}
                                >
                                  <DatePicker
                                    label="Test End Date"
                                    value={values.testEndDate}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={(newValue) => {
                                      // Ensure we're storing a valid dayjs object
                                      if (
                                        newValue &&
                                        dayjs(newValue).isValid()
                                      ) {
                                        setFieldValue(
                                          "testEndDate",
                                          dayjs(newValue)
                                        );

                                        // Run validation after value is set
                                        setTimeout(
                                          () =>
                                            setFieldTouched(
                                              "testEndDate",
                                              true,
                                              true
                                            ),
                                          10
                                        );
                                      } else {
                                        setFieldValue("testEndDate", null);
                                        setFieldTouched(
                                          "testEndDate",
                                          true,
                                          true
                                        );
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        error:
                                          touched.testEndDate &&
                                          Boolean(errors.testEndDate),
                                        helperText:
                                          (touched.testEndDate ||
                                            errors.testEndDate) &&
                                          errors.testEndDate ? (
                                            errors.testEndDate
                                          ) : (
                                            <span>
                                              Required
                                              <span className="text-red">
                                                *
                                              </span>
                                            </span>
                                          ),
                                        onBlur: () =>
                                          setFieldTouched(
                                            "testEndDate",
                                            true,
                                            true
                                          ),
                                      },
                                    }}
                                  />
                                </DemoContainer>
                              </LocalizationProvider>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid container sx={{ mb: 1, mt: 1.1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            label="Results"
                            id="results"
                            name="results"
                            value={values.results}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            // multiline
                            // rows={3}
                            error={touched.results && Boolean(errors.results)}
                            helperText={
                              <span>
                                Required<span className="text-red">*</span>
                              </span>
                            }
                            // InputLabelProps={{
                            //   shrink: true,
                            // }}
                          ></TextField>
                        </Grid>
                      </Grid>

                      {/* statisticalSignificance */}
                      <Grid container sx={{ mb: 1, mt: 0.5 }}>
                        <Grid sm={12} md={6} item>
                          <Autocomplete
                            id="statisticalSignificance"
                            options={statisticalOptions}
                            value={values.statisticalSignificance}
                            onChange={(event, newValue) => {
                              setFieldValue(
                                "statisticalSignificance",
                                newValue
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="statisticalSignificance"
                                label="Statistical Significance"
                                error={
                                  touched.statisticalSignificance &&
                                  Boolean(errors.statisticalSignificance)
                                }
                                helperText={
                                  <span>
                                    Required
                                    <span className="text-red">*</span>
                                  </span>
                                }
                                onBlur={handleBlur}
                              />
                            )}
                            clearIcon={
                              values.statisticalSignificance ? undefined : null
                            }
                          />
                        </Grid>
                      </Grid>

                      <Grid container sx={{ mb: 1 }}>
                        <Grid sm={12} md={6} item>
                          <TextField
                            fullWidth
                            id="recommendationsOrNotes"
                            name="recommendationsOrNotes"
                            label="Recommendations & Notes"
                            placeholder="Recommendations & Notes"
                            // helperText={
                            //   <span>
                            //     Required<span className="text-red">*</span>
                            //   </span>
                            // }
                            // InputLabelProps={{
                            //   shrink: true,
                            // }}
                            value={values.recommendationsOrNotes}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            // multiline
                            // rows={3}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>

                <Grid container spacing={0} sx={{ p: "0 10px 20px 10px" }}>
                  <Grid xs={12} item>
                    <Divider />
                  </Grid>
                </Grid>

                <Grid container spacing={0} sx={{ p: "10px 10px 0 10px" }}>
                  <Grid sm={12} item>
                    {/* <Typography variant="h3">ADD ATTACHMENTS</Typography> */}
                    {success && (
                      <Alert severity="success" sx={{ width: "100%", mb: 1.5 }}>
                        {successText}.
                      </Alert>
                    )}

                    {error && (
                      <Alert severity="error" sx={{ width: "100%", mb: 1.5 }}>
                        {error}
                      </Alert>
                    )}
                  </Grid>
                  <Grid sm={12} md={4.6} item>
                    <Box
                      className="FileUploadWrapper"
                      onDragEnter={handleDragEnter}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <Box className="fileUpload">
                        <UploadFileIcon className="iconUpload" />

                        <TextField
                          type="file"
                          variant="outlined"
                          inputProps={{ accept: "*/*", multiple: true }}
                          className="inputFileUpload"
                          onChange={handleInputChange}
                          id="uploadProfilePicture"
                          sx={{ display: "none" }}
                        />

                        <label htmlFor="uploadProfilePicture">
                          <Link className="">
                            <strong>Click to upload</strong>
                          </Link>{" "}
                          or drag and drop
                        </label>

                        <Typography className="lblfilesize">
                          {" "}
                          Allowed File sizes - Per file limit is 25 MB; total
                          files limit is 100 MB
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid sm={12} md={7.4} item>
                    <Box className="fileuploadPadding">
                      <Typography
                        variant="body1"
                        className="fw-600 fs-16 tt-uppercase mb-10"
                      >
                        {files.filter((file) => !file.isDelete)?.length > 0
                          ? files.filter((file) => !file.isDelete)?.length
                          : "No"}{" "}
                        {files.filter((file) => !file.isDelete)?.length === 1
                          ? "FILE"
                          : "FILES"}{" "}
                        Added
                      </Typography>
                    </Box>
                    <Box
                      className="fileDetailWrapper"
                      ref={fileDetailWrapperRef}
                      style={{ overflowY: "scroll", maxHeight: "500px" }}
                    >
                      {files
                        .filter((file) => !file?.isDelete)
                        .sort((a, b) => {
                          return (
                            new Date(a?.systemCreatedDateTime).getTime() -
                            new Date(b?.systemCreatedDateTime).getTime()
                          );
                        })
                        .map((file: any, visibleIndex) => {
                          const originalIndex = files.findIndex(
                            (f) => f === file
                          );
                          return (
                            <Box
                              className="d-flex justify-content-between fileUploadList"
                              id="scrollToTop"
                            >
                              <Box className="fileDetail d-flex">
                                <Box className="fileName">
                                  <Tooltip arrow title={file?.file?.name}>
                                    <Typography
                                      variant="body1"
                                      className="fs-16 text-blue text-ellipsis"
                                      sx={{ maxWidth: "330px" }}
                                    >
                                      {file?.filePath
                                        ?.split("/")
                                        .pop()
                                        .split("?")[0] || file?.file?.name}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="body2"
                                    className="fs-13 text-blue"
                                  >
                                    Uploaded by{" "}
                                    {file?.createdBy
                                      ? file?.createdBy
                                      : UserName}{" "}
                                    {file?.file?.size != 0 &&
                                      formatFileSize(file?.file?.size)}{" "}
                                    • Complete
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    className="fs-13 text-blue"
                                  >
                                    Uploaded on arafat{getFileUploadDate(file)}
                                  </Typography>
                                </Box>

                                <Box
                                  className="d-flex flex-direction-column justify-content-start"
                                  sx={{ position: "relative" }}
                                >
                                  {uploadType !== "research" &&
                                    (!changingStatusFiles[originalIndex] ? (
                                      
                                      <Box className="fileIcon">
                                        {file?.isSensitive ? (
                                          <Tooltip
                                            title="Marked sensitive"
                                            arrow
                                          >
                                            <Box
                                              sx={{
                                                minWidth: "170px",
                                                mb: 0.5,
                                              }}
                                              className="d-flex align-items-center"
                                            >
                                              <IconButton
                                                aria-label="Icon Sensitive File"
                                                size="small"
                                                className="icon-sensitive"
                                                onClick={() =>
                                                  handleToggleSensitivity(
                                                    file,
                                                    originalIndex
                                                  )
                                                }
                                              >
                                                <img
                                                  src={SensitiveIconSvg}
                                                  alt="Sensitive Icon"
                                                />
                                              </IconButton>
                                              <Typography
                                                variant="body2"
                                                className="fs-16"
                                                sx={{
                                                  color: colors.secondaryText,
                                                }}
                                              >
                                                Marked sensitive
                                              </Typography>
                                            </Box>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip
                                            title="Mark this file as Sensitive if it contains Personally Identifiable Information (PII)."
                                            arrow
                                            placement="top"
                                          >
                                            <Box
                                              sx={{ minWidth: "170px" }}
                                              className="d-flex align-items-center"
                                            >
                                              <IconButton
                                                aria-label="Icon Non Sensitive File"
                                                size="small"
                                                className="icon-sensitive"
                                                disabled={
                                                  Object.keys(
                                                    deletingFiles || {}
                                                  ).length > 0
                                                }
                                                onClick={() =>
                                                  handleToggleSensitivity(
                                                    file,
                                                    originalIndex
                                                  )
                                                }
                                              >
                                                <img
                                                  src={NonSensitiveIconBlueSvg}
                                                  alt="Non Sensitive Icon"
                                                />
                                              </IconButton>
                                              <Typography
                                                variant="body2"
                                                className="fs-16"
                                                sx={{
                                                  color: colors.secondaryText,
                                                }}
                                              >
                                                Mark as Sensitive
                                              </Typography>
                                            </Box>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ) : (
                                       
                                      <Box className="fileAction" sx={{minWidth: '170px'}}>
                                        <CircularProgress
                                          color="primary"
                                          size="1.5rem"
                                        />
                                      </Box> 
                                    ))}

                                  {!deletingFiles[originalIndex] ? (
                                    <Box className="d-flex align-items-center">
                                      <Box className="fileAction">
                                        <IconButton
                                          aria-label="delete"
                                          disabled={
                                            Object.keys(deletingFiles).length >
                                            0
                                          }
                                          size="medium"
                                          className="iconBtn"
                                          onClick={() =>
                                            handleFileDelete(
                                              file,
                                              originalIndex
                                            )
                                          }
                                        >
                                          <img
                                            src={DeleteIconSvg}
                                            alt="Delete Icon"
                                          />
                                        </IconButton>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        className="fs-16"
                                        sx={{
                                          color: colors.secondaryText,
                                          ml: 0.25,
                                        }}
                                      >
                                        Delete
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box className="fileAction">
                                      <CircularProgress
                                        color="primary"
                                        size="1.5rem"
                                      />
                                    </Box>
                                  )}
                                  {isLoading && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                      }}
                                    />
                                  )}                                 
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}

                      {isUploading &&
                        Object.entries(uploadingFiles).map(
                          ([fileId, { isUploading, filename }]) => (
                            <Box
                              className="d-flex justify-content-between fileUploadList"
                              id="scrollToTop"
                            >
                              <Box className="fileDetail d-flex">
                                <Box className="fileIcon">
                                  <UploadFileIcon className="iconUpload" />
                                </Box>
                                <Box className="fileName">
                                  <Typography
                                    variant="body1"
                                    className="fs-16 text-blue text-ellipsis"
                                    sx={{ maxWidth: "330px" }}
                                  >
                                    {filename.length > 40 ? (
                                      <Tooltip arrow title={filename}>
                                        <span style={{ cursor: "pointer" }}>
                                          {filename.substring(0, 40) + "..."}
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      filename
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    className="fs-13 text-blue"
                                  >
                                    Uploading
                                  </Typography>
                                </Box>
                              </Box>
                              {isUploading && (
                                <Box className="fileAction">
                                  <CircularProgress
                                    color="primary"
                                    size="1.5rem"
                                  />
                                </Box>
                              )}
                            </Box>
                          )
                        )}
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleUploadCancel}
                >
                  CANCEL
                </Button>

                <Button
                  size="small"
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    (uploadType == "research"
                      ? !isSubmitEnabledFormUpload(
                          isValid,
                          touched,
                          dirty,
                          errors,
                          values
                        )
                      : !isSubmitEnabledFileUpload(dirty, values)) ||
                    isLoading ||
                    isUploading ||
                    Object.keys(deletingFiles).length !== 0 ||
                    Object.keys(changingStatusFiles).length !== 0
                  }
                >
                  SAVE{" "}
                  {isLoading && (
                    <CircularProgress
                      size="1rem"
                      sx={{ color: colors.primaryDark, ml: 1 }}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </LocalizationProvider>
  );
};

export default ResearchExperimentsForm;
